import { useUI } from './store'

function getWsUrl(): string {
  const envUrl = import.meta.env.VITE_WS_URL as string | undefined
  const token = (useUI.getState().authToken || '').trim()
  const base = envUrl || `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`
  if (token) {
    const sep = base.includes('?') ? '&' : '?'
    return `${base}${sep}token=${encodeURIComponent(token)}`
  }
  return base
}

export type StreamAction = 'search' | 'outline' | 'write'

export type StreamHandlers = {
  onStarted: (action: StreamAction) => void
  onProgress: (event: string, text?: string, count?: number) => void
  onDelta: (delta: string, count?: number) => void
  onDone: (count?: number) => void
  onError: (error: string, details?: string) => void
}

let ws: WebSocket | null = null
let buffer = ''

export function stopStream() {
  try { ws?.close() } catch {}
  ws = null
}

async function normalizeMessage(ev: MessageEvent<any>): Promise<any> {
  const data = ev.data
  if (typeof data === 'string') return JSON.parse(data)
  if (data instanceof Blob) {
    const text = await data.text()
    return JSON.parse(text)
  }
  // ArrayBuffer
  if (data?.byteLength != null) {
    const text = new TextDecoder().decode(data)
    return JSON.parse(text)
  }
  return data
}

function openAndSend(payload: any, handlers: StreamHandlers) {
  buffer = ''
  const url = getWsUrl()
  ws = new WebSocket(url)
  ws.onopen = () => {
    ws?.send(JSON.stringify(payload))
  }
  ws.onmessage = async (ev) => {
    try {
      const msg = await normalizeMessage(ev)
      if (msg?.type === 'started') {
        handlers.onStarted(msg.action)
      } else if (msg?.type === 'progress') {
        handlers.onProgress(String(msg.event || ''), msg.text, msg.count)
      } else if (msg?.type === 'text.delta') {
        const d = String(msg.delta || '')
        buffer += d
        handlers.onDelta(d, msg.count)
      } else if (msg?.type === 'done') {
        handlers.onDone(msg.count)
      } else if (msg?.type === 'error') {
        handlers.onError(String(msg.error || 'error'), msg.details)
      }
    } catch (e: any) {
      handlers.onError('parse_error', e?.message || String(e))
    }
  }
  ws.onerror = (e: any) => {
    handlers.onError('ws_error', String(e?.message || 'WebSocket error'))
  }
  ws.onclose = (ev) => {
    // If server closed due to auth (policy violation), force logout so Login shows
    try {
      if (ev && (ev.code === 1008 || ev.reason === 'unauthorized')) {
        const st = useUI.getState()
        st.setIsStreaming?.(false as any)
        st.logout()
        alert('Session expired or unauthorized. Please sign in again.')
      }
    } catch {}
    ws = null
  }
  return () => {
    try { ws?.close() } catch {}
  }
}

export function startSearch(theme: string, handlers: StreamHandlers) {
  return openAndSend({ action: 'search', theme }, handlers)
}
export function startOutline(theme: string, data: string, handlers: StreamHandlers) {
  return openAndSend({ action: 'outline', theme, data }, handlers)
}
export function startWrite(plan: string, systemPrompt?: string, theme?: string, data?: string, title?: string, handlers?: StreamHandlers) {
  return openAndSend({ action: 'write', plan, systemPrompt, theme, data, title }, handlers!)
}

export function getBuffered(): string { return buffer }
